import { useCallback, useEffect, useMemo } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useLocation } from 'react-router-dom'

import { ConfirmationTemplateProps } from '../../../templates/Confirmation/index'
import { useDefaultLayoutProps } from '../../../layouts/DefaultLayout/useProps'
import { useResendTimeout } from '../../../hooks/useTimeout'
import { actions, selectors } from '../../../redux'

export const useAuthenticationProps = (): ConfirmationTemplateProps => {
  const layoutProps = useDefaultLayoutProps()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const verificationCodesService = useSelector(selectors.app.sendVerificationCodes)
  const authenticateVisitor = useSelector(selectors.app.authenticateVisitor)

  const firstSent = verificationCodesService.pending || verificationCodesService.complete

  // get email from query string
  const location = useLocation()
  const email = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('email')?.replace(/[^a-zA-Z0-9@._-]/g, '') || ''
  }, [location.search])

  // get id from URL params
  const params = useParams<{ id: string }>()
  const sendVerificationCode = useCallback(() => {
    if (params.id) {
      dispatch(
        actions.app.sendVerificationCodesRequest({
          visitor: params.id!,
        })
      )
    }
  }, [dispatch, params.id])

  const { isResendDisabled, setResendTimeout, countdown } = useResendTimeout()

  return useMemo(
    () => ({
      layout: layoutProps,
      ...(!firstSent
        ? {
            intro: {
              title: t('authentication.intro.title'),
              subtitle: t('authentication.intro.subtitle'),
              text: t('authentication.intro.text', { email }),
              button: {
                text: t('authentication.intro.button'),
                onClick: () => {
                  sendVerificationCode()
                },
              },
            },
          }
        : {
            form: {
              formik: {
                initialValues: {
                  emailCode: '',
                },
                validationSchema: Yup.object().shape({
                  emailCode: Yup.string()
                    .required()
                    .length(5, t('confirmation.form.validation.otp')),
                }),
                onSubmit: (data) => {
                  dispatch(
                    actions.app.authenticateVisitorRequest({
                      visitor: params.id!,
                      code: data?.emailCode ?? '',
                    })
                  )
                },
              },
              fields: {
                emailCode: {
                  name: 'emailCode',
                  numInputs: 5,
                },
              },
              title: t('authentication.form.title'),
              email: {
                title: t('authentication.form.email.title'),
                description: t('authentication.form.email.description', {
                  email,
                }),
              },
              resend: {
                label: isResendDisabled
                  ? t('authentication.form.resend.in', { countdown })
                  : t('authentication.form.resend'),
                disabled: isResendDisabled,
                onClick: () => {
                  setResendTimeout()
                  sendVerificationCode()
                },
              },
              submitButton: {
                isPending: authenticateVisitor.pending,
                text: t('authentication.form.submitButton'),
              },
              errors: authenticateVisitor.errors,
            },
          }),
    }),
    [
      layoutProps,
      firstSent,
      t,
      email,
      isResendDisabled,
      countdown,
      authenticateVisitor.pending,
      authenticateVisitor.errors,
      sendVerificationCode,
      dispatch,
      params.id,
      setResendTimeout,
    ]
  )
}
